import {
  ApiError,
  type ConsoleRelatedThread,
  type Thread,
  type ThreadDetail,
  type ThreadStatus,
} from '@central/api';
import { color, font } from '@central/ui';
import { Alert, Button, Modal, Select, TagsInput, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';

const statusWord: Record<ThreadStatus, string> = {
  open: 'Open',
  waiting_on_us: 'Waiting on us',
  waiting_on_customer: 'Waiting on customer',
  on_hold: 'On hold',
  closed: 'Resolved',
};

const MAX_TAGS = 10;

/** The words for a refused ticket reference, else the console-wide text. */
function refError(err: unknown): string {
  if (err instanceof ApiError && err.fields?.ref) {
    const why: Record<string, string> = {
      'not a ticket reference': 'Enter a ticket reference such as GC-1042.',
      'unknown ticket': 'No ticket has that reference.',
      'same ticket': 'That is this ticket.',
      "another customer's ticket": 'That ticket belongs to another customer.',
    };
    return why[err.fields.ref] ?? err.fields.ref;
  }
  return errorMessage(err, {
    merged: 'One of these tickets was merged into another; use that one.',
    is_duplicate: 'That ticket is itself closed as a duplicate.',
    crash_thread: 'An automatic crash report cannot be merged into another ticket.',
    too_many_related: 'One of these tickets has the most related tickets it can hold.',
  });
}

/** Refreshes every view of thread id and the lists after an organising change. */
function useRefresh() {
  const qc = useQueryClient();
  return (...ids: string[]) => {
    for (const id of ids) void qc.invalidateQueries({ queryKey: keys.thread(id), exact: true });
    void qc.invalidateQueries({ queryKey: [...keys.threads, 'list'] });
    void qc.invalidateQueries({ queryKey: [...keys.threads, 'count'] });
  };
}

/** The inbox's tag filter: one of the tags in use, or every ticket. */
export function TagFilter({ value, onChange }: { value: string; onChange: (tag: string) => void }) {
  const api = useApi();
  const tags = useQuery({
    queryKey: keys.threadTags(''),
    queryFn: () => api.threads.tags(),
    staleTime: 30_000,
  });
  const data = (tags.data?.items ?? []).map((s) => ({
    value: s.tag,
    label: `${s.tag} (${s.threads})`,
  }));
  if (value && !data.some((d) => d.value === value)) data.unshift({ value, label: value });
  return (
    <Select
      aria-label="Tag"
      placeholder="Any tag"
      size="sm"
      w={160}
      maw="100%"
      searchable
      clearable
      data={data}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
      nothingFoundMessage="No tags in use"
    />
  );
}

/** A merged or duplicate ticket says where its conversation went. */
export function TicketBanner({ d }: { d: ThreadDetail }) {
  const to = d.mergedInto ?? d.duplicateOf;
  if (!to) return null;
  return (
    <Alert color="gray" title={d.mergedInto ? 'Merged' : 'Duplicate'}>
      {d.mergedInto ? 'Merged into ' : 'Closed as a duplicate of '}
      <Link to="/inbox/$threadId" params={{ threadId: to.id }} search={(s) => s}>
        {to.ref}
      </Link>{' '}
      ({to.subject}).
      {d.mergedInto && ' The conversation continues there.'}
    </Alert>
  );
}

/** Tags, related tickets, and the duplicate and merge actions of one ticket. */
export function TicketOrganizer({ d }: { d: ThreadDetail }) {
  return (
    <section
      aria-label="Organise"
      style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}
    >
      <TicketTags t={d.thread} />
      <RelatedTickets d={d} />
      {!d.mergedInto && <MergeActions t={d.thread} />}
    </section>
  );
}

function TicketTags({ t }: { t: Thread }) {
  const api = useApi();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const prefix = search.trim().toLowerCase();
  const suggestions = useQuery({
    queryKey: keys.threadTags(prefix),
    queryFn: () => api.threads.tags(prefix),
    staleTime: 30_000,
  });
  const save = useMutation({
    mutationFn: (tags: string[]) => api.threads.update(t.id, { tags }),
    onSuccess: (next) => {
      qc.setQueryData<InfiniteData<ThreadDetail>>(keys.thread(t.id), (d) =>
        d ? { ...d, pages: d.pages.map((p) => ({ ...p, thread: next })) } : d,
      );
      void qc.invalidateQueries({ queryKey: [...keys.threads, 'list'] });
      void qc.invalidateQueries({ queryKey: [...keys.threads, 'tags'] });
    },
  });
  // TagsInput keeps an option's label as the tag, so options are bare tags and the
  // counts are only rendered.
  const counts = new Map((suggestions.data?.items ?? []).map((s) => [s.tag, s.threads]));
  const data = [...counts.keys()].filter((tag) => !t.tags.includes(tag));
  return (
    <div>
      <TagsInput
        label="Tags"
        description="Staff only; customers never see them."
        placeholder={t.tags.length < MAX_TAGS ? 'Add a tag' : undefined}
        size="sm"
        maxTags={MAX_TAGS}
        splitChars={[',', ' ']}
        data={data}
        renderOption={({ option }) => `${option.value} (${counts.get(option.value) ?? 0})`}
        value={t.tags}
        searchValue={search}
        onSearchChange={setSearch}
        disabled={save.isPending}
        onChange={(v) => {
          const tags = [...new Set(v.map((x) => x.trim().toLowerCase()).filter(Boolean))];
          if (tags.join() !== t.tags.join()) save.mutate(tags);
        }}
      />
      {save.error && (
        <Alert color="red" mt={6}>
          {save.error instanceof ApiError && save.error.fields?.tags
            ? 'A tag is 1 to 32 lowercase letters, digits or dashes; at most 10.'
            : errorMessage(save.error)}
        </Alert>
      )}
    </div>
  );
}

function RelatedTickets({ d }: { d: ThreadDetail }) {
  const api = useApi();
  const refresh = useRefresh();
  const t = d.thread;
  const related = d.related ?? [];
  const [ref, setRef] = useState('');
  const link = useMutation({
    mutationFn: (r: string) => api.threads.link(t.id, { ref: r }),
    onSuccess: () => {
      setRef('');
      refresh(t.id, ...related.map((r) => r.id));
    },
  });
  const unlink = useMutation({
    mutationFn: (other: ConsoleRelatedThread) => api.threads.unlink(t.id, other.id),
    onSuccess: (_, other) => refresh(t.id, other.id),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ref.trim()) link.mutate(ref.trim());
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontWeight: 600 }}>Related</span>
      {related.length === 0 ? (
        <span style={muted}>No related tickets.</span>
      ) : (
        <ul
          aria-label="Related tickets"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}
        >
          {related.map((r) => (
            <li
              key={r.id}
              style={{ display: 'flex', flexWrap: 'wrap', columnGap: 8, alignItems: 'center' }}
            >
              <Link
                to="/inbox/$threadId"
                params={{ threadId: r.id }}
                search={(s) => s}
                style={{ fontFamily: font.mono, color: color.text }}
              >
                {r.ref}
              </Link>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.subject}
              </span>
              <span style={muted}>
                {statusWord[r.status]}
                {r.customer.id !== t.customer.id && ` · ${r.customer.name}`}
              </span>
              <Button
                variant="subtle"
                size="compact-xs"
                aria-label={`Unlink ${r.ref}`}
                loading={unlink.isPending && unlink.variables?.id === r.id}
                onClick={() => unlink.mutate(r)}
              >
                Unlink
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
        <TextInput
          label="Link ticket"
          placeholder="GC-1042"
          size="xs"
          w={140}
          maxLength={20}
          value={ref}
          onChange={(e) => setRef(e.currentTarget.value)}
        />
        <Button type="submit" variant="default" size="xs" loading={link.isPending}>
          Link
        </Button>
      </form>
      {link.error && <Alert color="red">{refError(link.error)}</Alert>}
      {unlink.error && <Alert color="red">{errorMessage(unlink.error)}</Alert>}
    </div>
  );
}

type Action = 'duplicate' | 'merge';

const actionText: Record<Action, { button: string; title: string; confirm: string }> = {
  duplicate: {
    button: 'Mark duplicate of…',
    title: 'Mark as a duplicate',
    confirm: 'Mark duplicate',
  },
  merge: { button: 'Merge into…', title: 'Merge into another ticket', confirm: 'Merge' },
};

function MergeActions({ t }: { t: Thread }) {
  const api = useApi();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const [action, setAction] = useState<Action | null>(null);
  const [ref, setRef] = useState('');
  const run = useMutation({
    mutationFn: ({ a, r }: { a: Action; r: string }) =>
      a === 'merge'
        ? api.threads.merge(t.id, { ref: r })
        : api.threads.markDuplicate(t.id, { ref: r }),
    onSuccess: (target, { a }) => {
      setAction(null);
      setRef('');
      refresh(t.id, target.id);
      if (a === 'merge')
        void navigate({
          to: '/inbox/$threadId',
          params: { threadId: target.id },
          search: (s) => s,
        });
    },
  });
  // Crash reports and later occurrences point at an automatic crash thread.
  const canMerge = !(t.kind === 'crash' && t.startedBy === 'system');
  const open = (a: Action) => {
    run.reset();
    setRef('');
    setAction(a);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (action && ref.trim()) run.mutate({ a: action, r: ref.trim() });
  };
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <Button variant="default" size="xs" onClick={() => open('duplicate')}>
        {actionText.duplicate.button}
      </Button>
      {canMerge && (
        <Button variant="default" size="xs" onClick={() => open('merge')}>
          {actionText.merge.button}
        </Button>
      )}
      <Modal
        opened={action !== null}
        onClose={() => setAction(null)}
        title={action ? actionText[action].title : ''}
      >
        {action && (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 14 }}>
              {action === 'merge'
                ? `Every message of ${t.ref}, internal notes and attachments included, moves to the ticket you name. ${t.ref} is closed and keeps only a note pointing there. This cannot be undone.`
                : `${t.ref} is closed with a note, visible to ${t.customer.name}, naming the ticket you give, and the two are linked.`}
            </p>
            <TextInput
              label="Ticket"
              description={`One of ${t.customer.name}'s tickets`}
              placeholder="GC-1042"
              maxLength={20}
              data-autofocus
              value={ref}
              onChange={(e) => setRef(e.currentTarget.value)}
            />
            {run.error && <Alert color="red">{refError(run.error)}</Alert>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="default" onClick={() => setAction(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                color={action === 'merge' ? 'red' : 'dark'}
                loading={run.isPending}
                disabled={!ref.trim()}
              >
                {actionText[action].confirm}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
